import React, { useState } from "react";
import { motion } from "framer-motion";
import { Wrench, Battery, CircuitBoard, Calendar, Check, Phone } from "lucide-react";
import { STORES } from "../data/stores";
import { toast } from "sonner";
import { submitLead } from "../lib/leadsApi";

const SERVICES = [
  { id: "general", title: "General Service", icon: <Wrench size={20} />, desc: "Brake tune, drivetrain clean, tyre pressure and torque check." },
  { id: "battery", title: "Battery Health Check", icon: <Battery size={20} />, desc: "Cell-level diagnostics, charge cycles report and replacement options." },
  { id: "repair", title: "Repairs & Spares", icon: <CircuitBoard size={20} />, desc: "Genuine parts, motor swaps, controller fixes and frame work." },
  { id: "amc", title: "Annual Maintenance", icon: <Calendar size={20} />, desc: "Yearly plans with 4 free services, free pickup and battery cover." },
];

export default function Service() {
  const [form, setForm] = useState({ store: "", type: "", date: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.store || !form.type || !form.date) {
      toast.error("Please fill all fields to book a service slot.");
      return;
    }
    toast.success("Service slot requested. We'll confirm shortly.", {
      description: `${form.type} • ${form.store} • ${form.date}`,
    });
    submitLead({ type: "service", store: form.store, topic: form.type, date: form.date });
    setForm({ store: "", type: "", date: "" });
  };

  return (
    <div data-testid="service-page" className="bg-white">
      <section className="relative pt-16 pb-20 border-b border-slate-200 overflow-hidden bg-gradient-to-b from-[#F0F6FF] to-white">
        <div className="absolute inset-0 helix-bg" />
        <div className="container-x relative grid lg:grid-cols-2 gap-10 items-end">
          <div>
            <p className="eyebrow">Service</p>
            <h1 className="font-outfit text-5xl lg:text-7xl font-bold mt-5 leading-[0.98] tracking-tighter text-slate-900">
              Service that keeps <br />
              <span className="bg-gradient-to-r from-[#15B47E] to-[#2A8BFF] bg-clip-text text-transparent">every ride moving.</span>
            </h1>
          </div>
          <p className="text-slate-600 text-lg">
            Trained technicians, genuine parts and fast turnarounds at every eBikeGo store. Walk-in, request a pickup or schedule from your phone.
          </p>
        </div>
      </section>

      <section className="container-x py-20">
        <p className="eyebrow">What we offer</p>
        <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">Service catalogue.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="bg-white border border-slate-200 rounded-2xl p-7 group hover:border-[#2A8BFF]/40 hover:shadow-lg hover:-translate-y-1 transition-all"
            >
              <span className="w-10 h-10 rounded-full bg-[#2A8BFF]/10 text-[#1E6FE5] inline-flex items-center justify-center">{s.icon}</span>
              <h3 className="font-outfit font-bold text-xl mt-4 text-slate-900">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
              <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold group-hover:text-[#1E6FE5] transition">
                Book service →
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-[#F6F8FB] border-y border-slate-200 py-20">
        <div className="container-x">
          <p className="eyebrow">Our process</p>
          <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">Four simple steps.</h2>
          <div className="grid md:grid-cols-4 gap-6 mt-12 relative">
            {["Book a slot online", "Drop off or request pickup", "Diagnosis & estimate", "Pick up & ride"].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white border border-slate-200 rounded-2xl p-6"
              >
                <p className="text-[#1E6FE5] font-outfit text-2xl font-bold">0{i + 1}</p>
                <h4 className="font-outfit font-bold text-lg mt-3 text-slate-900">{step}</h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-20">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow">Book a service slot</p>
            <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
              Drop off your e-cycle. <br /> We'll handle the rest.
            </h2>
            <p className="text-slate-600 mt-5 max-w-md">
              Most general services finish the same day. We'll WhatsApp you a service summary with photos before we close the ticket.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { title: "Genuine parts", text: "Manufacturer-sourced, traceable" },
                { title: "Trained technicians", text: "Certified at every store" },
                { title: "Service history", text: "Online record per bike" },
                { title: "Fast turnaround", text: "Most jobs in under 4 hrs" },
              ].map((q) => (
                <div key={q.title} className="bg-white border border-slate-200 rounded-2xl p-5">
                  <Check size={16} className="text-[#15B47E]" />
                  <p className="text-sm font-bold mt-2 text-slate-900">{q.title}</p>
                  <p className="text-xs text-slate-500 mt-1">{q.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3 border border-slate-200 p-5 bg-white rounded-2xl hidden">
              <span className="w-12 h-12 rounded-full bg-[#3FE0A6]/15 text-[#15B47E] inline-flex items-center justify-center">
                <Phone size={20} />
              </span>
              <div>
                <p className="text-sm font-bold text-slate-900">Service helpline</p>
                <a href="tel:+918655692201" className="text-xs text-slate-500">+91 86556 92201</a>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white border border-slate-200 rounded-3xl p-8 shadow-[0_18px_50px_rgba(11,19,32,0.04)]"
            data-testid="service-booking-form"
          >
            <p className="eyebrow">Book service</p>
            <h3 className="font-outfit text-3xl font-bold mt-3 text-slate-900">Schedule in 30 seconds</h3>
            <div className="space-y-4 mt-6">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Select store</span>
                <select
                  value={form.store}
                  onChange={(e) => setForm({ ...form, store: e.target.value })}
                  className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none"
                  data-testid="service-store"
                >
                  <option value="">Choose a store…</option>
                  {STORES.map((s) => (
                    <option key={s.id} value={s.name}>{`${s.name} — ${s.city}`}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Service type</span>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none"
                  data-testid="service-type"
                >
                  <option value="">Choose service…</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>{s.title}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Preferred date</span>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none"
                  data-testid="service-date"
                />
              </label>
              <button type="submit" className="btn-primary w-full" data-testid="service-submit">
                Book Now
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
